import type {
  UpstreamAccountRecord,
  CreateUpstreamAccountRequest,
  CreateUpstreamAccountResponse,
  DeleteUpstreamAccountResponse,
  UpdateUpstreamAccountRequest,
  UpdateUpstreamAccountResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { createAccount, deleteAccount, listAccounts, updateAccount } from './upstreamAccounts.ts';

const ACCOUNTS_KEY = ['upstream-accounts'] as const;

export function useUpstreamAccountsList(upstreamId: string): UseQueryResult<readonly UpstreamAccountRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, upstreamId],
    queryFn: async () => {
      const res = await listAccounts(upstreamId, fetchImpl);
      return res.accounts;
    },
  });
}

export function useCreateUpstreamAccount(upstreamId: string): UseMutationResult<
  CreateUpstreamAccountResponse,
  Error,
  CreateUpstreamAccountRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUpstreamAccountRequest) =>
      createAccount(upstreamId, body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [...ACCOUNTS_KEY, upstreamId] }); },
  });
}

export function useUpdateUpstreamAccount(upstreamId: string): UseMutationResult<
  UpdateUpstreamAccountResponse,
  Error,
  { accountId: string; body: UpdateUpstreamAccountRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }) =>
      updateAccount(upstreamId, accountId, body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [...ACCOUNTS_KEY, upstreamId] }); },
  });
}

export function useDeleteUpstreamAccount(upstreamId: string): UseMutationResult<
  DeleteUpstreamAccountResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) =>
      deleteAccount(upstreamId, accountId, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [...ACCOUNTS_KEY, upstreamId] }); },
  });
}
